import { useState } from "react";
import { PendingDraftMatch } from "./DraftMatch.ts";
import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import { ArrowPathIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { cleanDocumentFileName } from "../../../../../lib/TFICLib.ts";

export type TransitionOutcome = 'rejected' | 'resolved';

export interface TransitioningDraft {
    draft: PendingDraftMatch;
    outcome: TransitionOutcome;
}

interface PendingDraftMatchesSectionProps {
    pendingDrafts: PendingDraftMatch[];
    transitioningDrafts: TransitioningDraft[];
    onRefresh: () => void;
    isRefreshing: boolean;
    onCancel: (draftMatchId: string) => Promise<void>;
}

function formatBookingDate(value: string): string {
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('nl-NL');
}

function DraftRow({ draft, rowClassName, onCancel }: { draft: PendingDraftMatch; rowClassName: string; onCancel?: (draftMatchId: string) => Promise<void> }) {
    const documentFilename = cleanDocumentFileName(draft.document_filename);
    const [isCancelling, setIsCancelling] = useState(false);

    async function handleCancel() {
        setIsCancelling(true);
        try {
            await onCancel!(draft.id);
        } finally {
            setIsCancelling(false);
        }
    }

    return (
        <li className={`border rounded-lg p-4 my-2 flex items-center justify-between transition-colors duration-700 ${rowClassName}`}>
            <div className="flex flex-col">
                <span className="font-medium">{draft.relation_name}</span>
                <span className="text-sm text-gray-600">{draft.description}</span>
                <span className="text-xs text-gray-500">{formatBookingDate(draft.booking_date)}</span>
            </div>
            <div className="flex items-center gap-6">
                <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 truncate max-w-xs" title={documentFilename}>
                    <DocumentTextIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {documentFilename}
                </span>
                <MoneyLabel value={draft.amount / 100} currency={draft.currency} colorAid={true} />
                {onCancel && (
                    <button
                        type="button"
                        aria-label="Koppeling annuleren"
                        onClick={handleCancel}
                        disabled={isCancelling}
                        className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer disabled:cursor-default disabled:opacity-50"
                    >
                        Annuleren
                    </button>
                )}
            </div>
        </li>
    );
}

export function PendingDraftMatchesSection({
    pendingDrafts,
    transitioningDrafts,
    onRefresh,
    isRefreshing,
    onCancel
}: PendingDraftMatchesSectionProps) {
    const hasContent = pendingDrafts.length > 0 || transitioningDrafts.length > 0;

    if (!hasContent) return null;

    return (
        <div className="mt-16">
            <div className="flex items-center justify-between mb-6">
                <h2 className="weight-600 text-2xl">Wacht op bevestiging</h2>
                <SecondaryButton
                    title="Vernieuwen"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    icon={(props) => (
                        <ArrowPathIcon {...props} className={`${props.className ?? ''} ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
                    )}
                />
            </div>

            <ul>
                {transitioningDrafts.map(({ draft, outcome }) => (
                    <DraftRow
                        key={`transition-${draft.id}`}
                        draft={draft}
                        rowClassName={outcome === 'rejected'
                            ? 'border-red-300 bg-red-100'
                            : 'border-green-300 bg-green-100'}
                    />
                ))}
                {pendingDrafts.map((draft) => (
                    <DraftRow
                        key={draft.id}
                        draft={draft}
                        rowClassName="border-gray-300 bg-white"
                        onCancel={onCancel}
                    />
                ))}
            </ul>
        </div>
    );
}
